import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  Grid,
  Box,
  Typography,
  Button,
} from '@material-ui/core';

import {
  MultiSelect,
  Select,
  Editor,
} from '../common/fields';
import FilePicker from '../common/filePicker';
import FilePickerMulti from '../common/filePickerMulti';
import Alert from '../common/alert';

import Markets from '../itemFamily/Markets';
import ActionBar from '../itemFamily/ActionBar';
import MainImage from '../itemFamily/MainImage';
import MasterCountryTooltip from '../itemFamily/MasterCountryTooltip';

import { enqueueSnackbar } from '../snackbar/snackbarSlice';
import { selectWheelGlobals } from '../app/appSlice';
import {
  selectMasterCountry,
  selectCurrentCountry,
  selectCountriesWithNames,
} from '../countries/countriesSlice';
import {
  selectSaveStatus,
  clearSaveStatus,
  saveWheelDraft,
  saveWheelPublished,
  publishWheel,
  selectEqualStatus,
  selectMaster,
} from './wheelSlice';
import { selectItemFamilies } from '../itemFamilies/itemFamiliesSlice';

import { defaultStudioFolderPC24 } from '../../constants';
import SuitableTyreSizes from '../common/suitableTyreSizes';

const defaultStudioFolder = defaultStudioFolderPC24;

const WheelPagePC24 = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { itemOwnerId } = useParams();

  const { status, wheel, canEdit: userAllowedToEdit } = props;

  const { saveStatus, saveError } = useSelector(selectSaveStatus);
  const masterWheel = useSelector(selectMaster);
  const {
    machines = [],
    features = [],
    brands = [],
    workTypes = [],
    feeds = [],
    productLines = [],
    tyreSizes = [],
  } = useSelector(selectWheelGlobals);
  const masterCountry = useSelector(selectMasterCountry);
  const currentCountry = useSelector(selectCurrentCountry);
  const allCountries = useSelector(selectCountriesWithNames);
  const equalStatus = useSelector(selectEqualStatus);
  const { itemFamilies } = useSelector(selectItemFamilies);

  const [ wheelData, setWheelData ] = useState(wheel || {});
  const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);

  const handleChange = (field, value) => {
    setWheelData({
      ...wheelData,
      [field]: value
    });
    setHasUnsavedChanges(true);
  }

  const handleFieldChange = event => {
    handleChange(event.target.name, event.target.value);
  }

  const saveDraft = () => {
    // Save to server
    if(status === 'draft') {
      dispatch(saveWheelDraft({itemOwnerId, wheelId: wheel.code, wheelData: wheelData}));
    }
  };

  const saveDraftAndPublish = () => {
    // Save to server
    if(status === 'draft') {
      dispatch(publishWheel({itemOwnerId, wheelId: wheel.code, wheelData: wheelData}));
    }
  }

  const savePublished = () => {
    // Save to server
    if(status === 'published') {
      dispatch(saveWheelPublished({itemOwnerId, wheelId: wheel.code, wheelData: wheelData}));
    }
  };

  const cancelChanges = () => {
    setWheelData(wheel);
    setHasUnsavedChanges(false);
  };

  const renderTooltip = (content, type) => {
    if(!content || content.length === 0) {
      return null;
    }

    if(currentCountry === masterCountry) {
      return null;
    }

    return <MasterCountryTooltip content={content} type={type} masterCountry={masterCountry} />;
  }

  useEffect(() => {
    // Parent props change - basically draft vs publish change
    setWheelData(wheel);
    setHasUnsavedChanges(false);
  }, [wheel]);

  useEffect(() => {
    // Show feedback on save status
    if(saveStatus === 'failed') {
      dispatch(enqueueSnackbar({
        message: t(`errors:${saveError.message}`),
        options: {
          variant: 'error'
        }
      }));
      dispatch(clearSaveStatus());
    } else if(saveStatus === 'succeeded') {
      dispatch(enqueueSnackbar({
        message: t('common:savedSuccessfully'),
        options: {
          variant: 'success'
        }
      }));
      dispatch(clearSaveStatus());
      setHasUnsavedChanges(false);
    }
  }, [saveStatus]);

  if(!wheel.code) {
    const message = status === 'published' ? 'notPublished' : 'notFound';
    return <Alert severity="info">{t(`wheel:${message}`)}</Alert>
  } else {

    return (
      <>
        <Grid container spacing={7}>
          <Grid item xs={12} md={8}>
            {status === 'draft' ? (equalStatus ? null : <Box mb={2}><Alert severity="info">{t('common:containsUnpunblishedChanges')}</Alert></Box>) : null}

            <Box mb={2}>
              <Typography variant="subtitle1">{t('wheel:wheel')}</Typography>
              <Typography variant="h4" component="h1">{wheel.name}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="overline" display="block">{t('wheel:lastChanged')}</Typography>
              <Typography variant="caption">{wheel.updated}</Typography>
            </Box>

            <Box my={5}>
              <Box my={3}>
                <Select
                  label={t('wheel:brand')}
                  name="brand"
                  value={wheelData.brand}
                  options={brands.map(item => ({label: item.name, value: item.code}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
              <Box my={3}>
                <MultiSelect
                  label={t('wheel:features')}
                  name="features"
                  value={wheelData.features}
                  options={features.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
              <Box my={3}>
                <MultiSelect
                  label={t('wheel:machines')}
                  name="machines"
                  value={wheelData.machines}
                  options={machines.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
              <Box my={3}>
                <MultiSelect
                  label={t('wheel:workTypes')}
                  name="workTypes"
                  value={wheelData.workTypes}
                  options={workTypes.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
              <Box my={3}>
                <MultiSelect
                  label={t('wheel:itemFamilies')}
                  name="itemFamilies"
                  value={wheelData.itemFamilies}
                  options={itemFamilies.map(item => ({label: item.name, value: item.code}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
              <Box my={3}>
                <MultiSelect
                  label={t('wheel:productLines')}
                  name="productLines"
                  value={wheelData.productLines}
                  options={productLines.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
            </Box>

            <Box my={5}>
              <Markets allCountries={allCountries} selectedCountries={wheelData.markets} handleChange={newVal => handleChange('markets', newVal)} disabled={!userAllowedToEdit} />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('wheel:feeds')}</Typography></Box>
              <MultiSelect
                label={t('wheel:chooseFeeds')}
                name="feeds"
                value={wheelData.feeds}
                options={feeds.map(item => ({label: item.name, value: item.id}))}
                onChange={handleFieldChange}
                disabled={!userAllowedToEdit}
              />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('wheel:content')}</Typography></Box>
              <Box my={3}>
                <Editor
                  label={t('wheel:marketingText')}
                  name="marketingText"
                  value={wheelData.marketingText}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterWheel.marketingText, 'html')}
                  maxLength={500}
                />
              </Box>
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('wheel:images')}</Typography></Box>

              {['imageRender', 'imageCut3d'].map(field =>
                <FilePicker
                  key={field}
                  fieldName={t(`wheel:${field}`)}
                  fieldValue={wheelData[field]}
                  setFieldValue={(file) => handleChange(field, file)}
                  disabled={!userAllowedToEdit}
                  rootFolderId={defaultStudioFolder}
                />
              )}

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('wheel:otherImages')}</Typography></Box>

              <FilePickerMulti
                fieldName={t('wheel:otherImages')}
                fieldValue={wheelData.images}
                setFieldValue={(files) => handleChange('images', files)}
                btnLabel={t('wheel:addImages')}
                disabled={!userAllowedToEdit}
                rootFolderId={defaultStudioFolder}
              />
            </Box>

            <Box my={5}>
              <SuitableTyreSizes
                allSizes={tyreSizes}
                selectedSizes={wheelData.suitableTyreSizes}
                handleChange={newVal => handleChange('suitableTyreSizes', newVal)}
                disabled={!userAllowedToEdit}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <MainImage src={wheelData.imageMain?.medium} />

            <FilePicker
              fieldName={t('wheel:imageMain')}
              fieldValue={wheelData.imageMain}
              setFieldValue={(file) => handleChange('imageMain', file)}
              disabled={!userAllowedToEdit}
              rootFolderId={defaultStudioFolder}
            />

            {status === 'published' ? (
              <Box mt={4}>
                <Box mb={2}><Typography variant="h6" component="h2">{t('itemFamily:generatePdfs')}</Typography></Box>
                {wheelData.pdfGenerationLinks?.map(link => <Button key={link.name} variant="contained" color="primary" disableElevation component={'a'} href={link.url} target="_blank">{link.name}</Button>)}
              </Box>
            ) : null}
          </Grid>

        </Grid>
        {hasUnsavedChanges
          ?
          <ActionBar>
            <Button variant="outlined" color="primary" onClick={cancelChanges}>{t('common:cancelChanges')}</Button>
            {
              status === 'draft' ?
                <>
                  <Button variant="contained" color="primary" onClick={saveDraft} disabled={saveStatus === 'loading'}>{t('common:saveChanges')}</Button>
                  <Button variant="contained" color="primary" onClick={saveDraftAndPublish} disabled={saveStatus === 'loading'}>{t('common:saveAndPublishChanges')}</Button>
                </>
                :
                <Button variant="contained" color="primary" onClick={savePublished} disabled={saveStatus === 'loading'}>{t('common:publishChanges')}</Button>
            }

          </ActionBar>
          :
          null
        }
      </>
    );
  }
};

WheelPagePC24.propTypes = {
  status: PropTypes.oneOf(['draft', 'published']).isRequired,
  wheel: PropTypes.object,
};

export default WheelPagePC24;
